import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

const CircleAreaCalculator = () => {
  const [radius, setRadius] = useState('');
  const [diameter, setDiameter] = useState('');
  const [area, setArea] = useState('');
  const [circumference, setCircumference] = useState('');
  const [error, setError] = useState('');
  const [showChart, setShowChart] = useState(false);

  const calculateArea = () => {
    let r = parseFloat(radius);
    let d = parseFloat(diameter);

    if (!radius && !diameter) {
      setError('Please enter either radius or diameter');
      return;
    }

    if (radius && diameter) {
      setError('Please enter only one value (radius or diameter)');
      return;
    }

    if (radius) {
      if (r <= 0) {
        setError('Radius must be greater than 0');
        return;
      }
      setArea((r * r * Math.PI).toFixed(2));
      setCircumference((2 * Math.PI * r).toFixed(2));
    } else if (diameter) {
      if (d <= 0) {
        setError('Diameter must be greater than 0');
        return;
      }
      setArea(((d / 2) * (d / 2) * Math.PI).toFixed(2));
      setCircumference(Math.PI * d).toFixed(2);
    }

    setError('');
    setShowChart(true);
  };

  const chartData = {
    labels: ['Circle Area'],
    datasets: [
      {
        data: [parseFloat(area) || 0],
        backgroundColor: ['#4CAF50'],
        borderColor: ['#4CAF50'],
        borderWidth: 1,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: true,
      },
    },
  };

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-md">
      <h1 className="text-2xl font-bold mb-6 text-center">Circle Area Calculator</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div>
          <label htmlFor="radius" className="block text-sm font-medium mb-1">
            Radius (optional)
          </label>
          <input
            type="number"
            id="radius"
            value={radius}
            onChange={(e) => setRadius(e.target.value)}
            placeholder="Enter radius"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="diameter" className="block text-sm font-medium mb-1">
            Diameter (optional)
          </label>
          <input
            type="number"
            id="diameter"
            value={diameter}
            onChange={(e) => setDiameter(e.target.value)}
            placeholder="Enter diameter"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      <button
        onClick={calculateArea}
        className="w-full bg-blue-500 text-white py-2 px-4 rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
      >
        Calculate Area
      </button>

      {area && (
        <div className="mt-6">
          <h2 className="text-xl font-semibold mb-4">Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Area</h3>
              <p className="text-xl">{area} square units</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h3 className="text-lg font-semibold mb-2">Circumference</h3>
              <p className="text-xl">{circumference} units</p>
            </div>
          </div>

          {showChart && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-4">Visual Representation</h3>
              <div className="w-full h-64">
                <Doughnut data={chartData} options={chartOptions} />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CircleAreaCalculator;
